/**
 * i18n bootstrap (Commerce — Taquillera VIP) — Ola 3 client localization.
 *
 * es baseline (complete for the surfaces we touch); en full parity. Language is
 * detected from localStorage → browser, persisted to localStorage, and switched
 * at runtime without reload. The active language drives `Accept-Language`.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import esCommon from './locales/es/common.json';
import esEvents from './locales/es/events.json';
import esCheckout from './locales/es/checkout.json';
import esWallet from './locales/es/wallet.json';

import enCommon from './locales/en/common.json';
import enEvents from './locales/en/events.json';
import enCheckout from './locales/en/checkout.json';
import enWallet from './locales/en/wallet.json';

export const SUPPORTED_LANGUAGES = ['es', 'en'] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_STORAGE_KEY = 'fc.lang';

export const NAMESPACES = ['common', 'events', 'checkout', 'wallet'] as const;

const resources = {
  es: { common: esCommon, events: esEvents, checkout: esCheckout, wallet: esWallet },
  en: { common: enCommon, events: enEvents, checkout: enCheckout, wallet: enWallet },
} as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'es',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    ns: NAMESPACES as unknown as string[],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    returnNull: false,
  });

/** Normalize i18n.language → an AppLanguage ('es' | 'en'). */
export function activeLanguage(): AppLanguage {
  const lang = (i18n.language || 'es').toLowerCase();
  return lang.startsWith('en') ? 'en' : 'es';
}

export default i18n;
